import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import { shopifyFetch, COLLECTIONS_QUERY, nodes } from "@/lib/shopify";
import { Reveal, Eyebrow } from "@/components/Reveal";

const PREFERRED_COVER = { "weird-clocks": "vinyl-record-clock-with-stand" };

export default function CollectionsIndexPage() {
  const [collections, setCollections] = useState([]);
  const [state, setState] = useState("loading");

  useEffect(() => {
    shopifyFetch(COLLECTIONS_QUERY, { first: 20 })
      .then((d) => {
        setCollections(nodes(d.collections).filter((c) => c.handle !== "frontpage"));
        setState("ready");
      })
      .catch(() => setState("error"));
  }, []);

  return (
    <main data-testid="collections-index-page" className="bg-void pt-28 sm:pt-32">
      <Seo
        title="Collections — Weird O'Clock"
        siteName="Weird O'Clock"
        description="Every Weird O'Clock collection: surreal clocks, odd decor, mirrors and more."
      />
      <div className="mx-auto max-w-[1400px] px-5 pb-24 sm:px-8">
        <Reveal>
          <Eyebrow>Browse</Eyebrow>
          <h1 className="font-display text-4xl font-black tracking-tight text-ivory sm:text-5xl lg:text-6xl">
            Every flavour of <span className="font-editorial italic text-amber">weird</span>
          </h1>
        </Reveal>

        {state === "loading" && (
          <div className="flex items-center gap-3 py-24 text-ivory/40">
            <Loader2 className="h-5 w-5 animate-spin text-amber" />
            <span className="font-mono2 text-xs uppercase tracking-[0.25em]">Loading collections…</span>
          </div>
        )}
        {state === "error" && (
          <p className="mt-10 rounded-lg border border-amber/30 bg-amber/5 px-5 py-4 font-mono2 text-xs uppercase tracking-[0.2em] text-amber">
            Connecting to the Weird O'Clock Shopify store — check back in a tick.
          </p>
        )}

        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {collections.map((c, i) => {
            const candidates = (c.products?.edges || []).map((e) => e.node);
            const pick =
              candidates.find((p) => p.handle === PREFERRED_COVER[c.handle]) ||
              candidates.find((p) => !/dog|puppy|dachshund|labrador/i.test(p.handle)) ||
              candidates[0];
            const img = c.image?.url || pick?.featuredImage?.url;
            return (
              <Reveal key={c.id} delay={i * 0.06}>
                <Link
                  to={`/collections/${c.handle}`}
                  data-testid={`collections-index-card-${c.handle}`}
                  className="group relative block aspect-[4/3] overflow-hidden rounded-xl border border-white/10"
                >
                  {img && (
                    <img
                      src={img}
                      alt={c.image?.altText || c.title}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-108"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-void/90 via-void/20 to-transparent" />
                  <div className="absolute bottom-0 p-6">
                    <h2 className="font-display text-2xl font-extrabold text-ivory group-hover:text-amber">
                      {c.title}
                    </h2>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </main>
  );
}
